import { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import { useInstitution } from "../institution";
import { useAllAccounts } from "../accounts";

import { useBaseRowActions } from "./bulkBase";
import { BulkPaymentForm, BulkRowInitialValue } from "./BulkPayment.form";
import {
  selectAllBulkPayments,
  selectBulkPaymentById,
  selectBulkPaymentsFilters,
  selectBulkPaymentsTotal,
  selectFormattedBulkPayments,
  selectSelectedBulkPayment,
  selectFormattedPaginatedBulkPayments,
} from "./selectors";
import { formatBulkPayment, getBulkPaymentStartDate } from "./helpers";
import { useFetchBulkPaymentById } from "./requests";

import type { FormattedBulkPayment } from "./types";

export const useCreateBulkPaymentRowActions = () => {
  const institution = useInstitution();
  const form = BulkPaymentForm.useForm();
  const achNextCutoff = institution?.ach_next_cutoff;
  const expeditedPaymentsFeature = institution?.features?.expedited_payments;
  const minDate = getBulkPaymentStartDate(
    expeditedPaymentsFeature,
    achNextCutoff,
    institution?.features?.fednow_send,
  );

  const { addRow: baseAddRow, ...rowActions } = useBaseRowActions(form);

  const addRow = () => {
    baseAddRow({ ...BulkRowInitialValue, scheduledDate: minDate });
  };

  return {
    addRow,
    ...rowActions,
  };
};

export const useBulkPayments = () => {
  return useSelector(selectAllBulkPayments);
};

export const useFormattedPaginatedBulkPayments = (pageNumber: number) => {
  return useSelector((state) =>
    selectFormattedPaginatedBulkPayments(state, pageNumber),
  );
};

export const useBulkPaymentById = (uuid: string) => {
  return useSelector(selectBulkPaymentById(uuid));
};

export const useBulkPaymentsFilters = () => {
  return useSelector(selectBulkPaymentsFilters);
};

export const useBulkPaymentsTotal = () => {
  return useSelector(selectBulkPaymentsTotal);
};

export const useSelectedBulkPayment = () => {
  return useSelector(selectSelectedBulkPayment);
};

export const useFormattedBulkPayments = () => {
  return useSelector(selectFormattedBulkPayments);
};

// resolve bulk payment to a `FormattedBulkPayment`, preferring (in
// order): an already-formatted payment passed in, the raw payment cached in the
// store, then a fetch by uuid (for deep links where the list was never loaded)
export const useResolvedBulkPayment = ({
  bulkPayment = null,
  bulkPaymentId,
}: {
  // already-formatted bulk payment, used as-is when provided
  bulkPayment?: FormattedBulkPayment | null;
  bulkPaymentId?: API.BulkPaymentId | null;
}): FormattedBulkPayment | null => {
  const accounts = useAllAccounts();
  const cachedRaw = useSelector(selectBulkPaymentById(bulkPaymentId ?? "")) as
    | API.BulkPayment
    | undefined;
  const { send: fetchBulkPaymentById } = useFetchBulkPaymentById();
  const [fetched, setFetched] = useState<API.BulkPayment | null>(null);

  const hasResolvableSource = Boolean(bulkPayment) || Boolean(cachedRaw);

  useEffect(() => {
    if (hasResolvableSource || !bulkPaymentId) {
      setFetched(null);
      return undefined;
    }
    let cancelled = false;
    fetchBulkPaymentById(bulkPaymentId)
      .then((response) => {
        if (!cancelled) setFetched(response);
      })
      .catch(() => {
        if (!cancelled) setFetched(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bulkPaymentId, hasResolvableSource, fetchBulkPaymentById]);

  return useMemo(() => {
    if (bulkPayment) return bulkPayment;
    const payment = cachedRaw ?? fetched;
    return payment ? formatBulkPayment(payment, accounts) : null;
  }, [bulkPayment, cachedRaw, fetched, accounts]);
};
